'use client';

import { pdfWorkerSrc } from '@/lib/utils/pdfWorker';
import { AlertCircle, File, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { memo, useCallback, useState } from 'react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import type { PdfViewerProps } from './types';

// `Document`/`Page` are loaded via `next/dynamic({ ssr: false })` — same
// reason the prior `@react-pdf-viewer/core` `Worker`/`Viewer` pair used the
// identical pattern here (pdfjs relies on browser APIs unavailable during
// SSR). See `elements/pdf-viewer/pdf-viewer.tsx` for the sibling usage.
const Document = dynamic(
  () =>
    import('react-pdf').then((mod) => {
      mod.pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc();
      return mod.Document;
    }),
  { ssr: false }
);

const Page = dynamic(
  () =>
    import('react-pdf').then((mod) => {
      mod.pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc();
      return mod.Page;
    }),
  { ssr: false }
);

/**
 * PDF Viewer component with loading and error states
 */
const PdfViewer = memo(({ fileUrl, isLoading, error }: PdfViewerProps) => {
  const t = useTranslations('Common');
  const [internalError, setInternalError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);

  const handleLoadSuccess = useCallback(
    ({ numPages: loadedPages }: { numPages: number }) => {
      setInternalError(null);
      setNumPages(loadedPages);
    },
    []
  );

  const handleLoadError = useCallback(
    (err: Error) => {
      setInternalError(err.message || t('unknownErrorOccurred'));
    },
    [t]
  );

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center h-full p-6'>
        <AlertCircle className='w-12 h-12 text-status-error mb-4' />
        <h3 className='text-lg font-semibold text-status-error'>
          {t('errorLoadingDocument')}
        </h3>
        <p className='text-sm text-status-error mt-2'>{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='flex flex-col items-center justify-center h-full'>
        <Loader2 className='w-12 h-12 text-status-info animate-spin mb-4' />
        <p className='text-sm text-content-secondary'>{t('loadingDocument')}</p>
      </div>
    );
  }

  if (!fileUrl) {
    return (
      <div className='flex flex-col items-center justify-center h-full'>
        <File className='w-16 h-16 text-content-muted mb-4' />
        <p className='text-content-muted'>{t('noDocumentAvailable')}</p>
      </div>
    );
  }

  if (internalError) {
    return (
      <div className='flex flex-col items-center justify-center h-full p-8'>
        <AlertCircle className='w-12 h-12 text-status-error mb-4' />
        <p className='text-lg text-status-error'>{t('failedToLoadPdf')}</p>
        <p className='text-sm text-content-muted mt-4'>{internalError}</p>
      </div>
    );
  }

  return (
    <div className='h-full overflow-auto bg-surface-subtle'>
      <Document
        key={fileUrl}
        file={fileUrl}
        onLoadSuccess={handleLoadSuccess}
        onLoadError={handleLoadError}
        loading={
          <div className='flex items-center justify-center h-full py-8'>
            <Loader2 className='w-8 h-8 text-status-info animate-spin' />
          </div>
        }
      >
        {Array.from({ length: numPages }, (_, idx) => (
          <div key={`page_${idx + 1}`} className='mx-auto mb-2 w-fit'>
            <Page pageNumber={idx + 1} />
          </div>
        ))}
      </Document>
    </div>
  );
});

PdfViewer.displayName = 'PdfViewer';

export default PdfViewer;
